/**
 * Stripe's vocabulary as this product stores it, and the one convention that names a price.
 *
 * `subscriptions.status` mirrors Stripe and is never shown to a customer; the state a
 * customer reads is `organizations.billing_status` (`tiers.ts`). Ruled 2026-10-26 — Drift
 * Log §U58.
 */
import { PLANS } from "./plans.js";
import { SELF_SERVE_TIERS } from "./tiers.js";
/**
 * Stripe's eight subscription statuses, verbatim — `Stripe.Subscription.Status` in
 * stripe-node 23.0.1, API `2026-09-30.endive`. Held set-equal to `0030`'s CHECK.
 */
export const SUBSCRIPTION_STATUSES = [
    "incomplete",
    "incomplete_expired",
    "trialing",
    "active",
    "past_due",
    "canceled",
    "unpaid",
    "paused",
];
export function isSubscriptionStatus(value) {
    return SUBSCRIPTION_STATUSES.includes(value);
}
/**
 * The statuses that grant the subscription's tier. Every other status records the row and
 * moves no plan — U60 adds those arms.
 */
export const ENTITLING_SUBSCRIPTION_STATUSES = [
    "active",
    "trialing",
];
/** The statuses after which a subscription bills nothing and Checkout may open a new one. */
export const ENDED_SUBSCRIPTION_STATUSES = [
    "canceled",
    "incomplete_expired",
];
export const BILLING_INTERVALS = ["month", "year"];
/**
 * The tiers a Checkout Session can sell: self-serve, and not free. A literal so a schema can
 * enumerate it; {@link isCheckoutTier} states the rule it must equal over `PLANS`.
 */
export const CHECKOUT_TIERS = [
    "starter",
    "growth",
    "scale",
];
/** The rule {@link CHECKOUT_TIERS} spells out: self-serve, with a price above zero. */
export function isCheckoutTier(tier) {
    return SELF_SERVE_TIERS.includes(tier) && (PLANS[tier].monthlyUsd ?? 0) > 0;
}
/**
 * The Stripe client's host options for `STRIPE_API_BASE`, the e2e fixture's stub, or none.
 * One spelling for `apps/api` and `apps/worker`, which may not import each other.
 */
export function stripeHostOptions(apiBase) {
    if (apiBase === undefined || apiBase.trim() === "")
        return {};
    const base = new URL(apiBase);
    return {
        host: base.hostname,
        port: base.port,
        protocol: base.protocol === "http:" ? "http" : "https",
    };
}
const LOOKUP_KEY_PREFIX = "packagepage_";
/**
 * The Stripe Price `lookup_key` for one tier and interval — `packagepage_starter_month`.
 *
 * Prices are found by this key, so no price id is configured anywhere. The account's six
 * prices must carry these keys.
 */
export function stripeLookupKey(tier, interval) {
    return `${LOOKUP_KEY_PREFIX}${tier}_${interval}`;
}
/** The inverse of {@link stripeLookupKey}, or `null` for a key this product did not mint. */
export function tierFromLookupKey(key) {
    if (key === null || key === undefined)
        return null;
    for (const tier of CHECKOUT_TIERS) {
        for (const interval of BILLING_INTERVALS) {
            if (key === stripeLookupKey(tier, interval))
                return { tier, interval };
        }
    }
    return null;
}
//# sourceMappingURL=billing.js.map